const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// --- Generic Helper Functions ---

const request = async (endpoint, options = {}, accessToken) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    if (response.status === 204) return null;
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData));
  }
  return response.status === 204 ? null : response.json();
};

const publicRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData));
  }
  return response.json();
};

const get = (endpoint, accessToken) => {
  return request(endpoint, { method: 'GET' }, accessToken);
};

const post = (endpoint, body, accessToken) => {
  return request(
    endpoint,
    { method: 'POST', body: JSON.stringify(body) },
    accessToken
  );
};

const del = (endpoint, accessToken) => {
  return request(endpoint, { method: 'DELETE' }, accessToken);
};

// --- Specific Exported Functions ---

// Public
export const getPublicPersona = (username) => {
  return publicRequest(`/v1/public/p/${username}`);
};

export const getPublicPersonaImageUrl = (username) => {
  return publicRequest(`/v1/public/personas/${username}/image-url`);
};

export const postPublicChatCompletion = (payload) => {
  return publicRequest('/v1/public/chat/completions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const checkUsernameAvailability = async (username) => {
  return publicRequest(`/v1/public/personas/username-check/${username}`, {
    method: 'GET',
  });
};

// For Users
export const linkAccount = (payload, accessToken) => {
  return post('/v1/user/link-account', payload, accessToken);
};

export const getUserStatus = (accessToken) => {
  return get('/v1/user/auth/status', accessToken);
};

export const acceptTerms = (payload, accessToken) => {
  return post('/v1/user/accept-terms', payload, accessToken);
};

export const getUserProfile = (accessToken) => {
  return get('/v1/user/profile', accessToken);
};

export const updateUserProfile = (payload, accessToken) => {
  return put('/v1/user/profile', payload, accessToken);
};

export const initializeDigitalLegacy = (accessToken) => {
  return post('/v1/user/digital-legacy/initialize', {}, accessToken);
};

export const getDigitalLegacy = (accessToken) => {
  return get('/v1/user/digital-legacy', accessToken);
};

export const submitPersonalityTest = (payload, accessToken) => {
  return post('/v1/user/submit-test', payload, accessToken);
};

export const getNarrativeQuestion = (accessToken) => {
  return get('/v1/user/narrative-question', accessToken);
};

export const saveNarrativeAnswer = (payload, accessToken) => {
  return post('/v1/user/digital-legacy/answer', payload, accessToken);
};

export const analyzeStyle = (payload, accessToken) => {
  return post('/v1/user/analyze-style', payload, accessToken);
};

// For Chat
export const postPrivateChatCompletion = (payload, accessToken) => {
  return post('/v1/chat/completions', payload, accessToken);
};

export const generateStudioContent = (payload, accessToken) => {
  return post('/v1/chat/generate', payload, accessToken);
};

export const getPresignedUploadUrl = (payload, accessToken) => {
  return post('/v1/uploads/presigned-url', payload, accessToken);
};

// For Personas ("Creations")
export const createPersona = (payload, accessToken) => {
  return post('/v1/personas', payload, accessToken);
};

export const createImageUploadUrl = (payload, accessToken) => {
  return post('/v1/personas/image-upload-url', payload, accessToken);
};

export const getPersonaImageUrl = (personaId, accessToken) => {
  return request(
    `/v1/personas/${personaId}/image-url`,
    { method: 'GET' },
    accessToken
  );
};

export const analyzeTextForPersona = (payload, accessToken) => {
  return post('/v1/personas/analyze-text', payload, accessToken);
};

const put = (endpoint, body, accessToken) => {
  return request(
    endpoint,
    { method: 'PUT', body: JSON.stringify(body) },
    accessToken
  );
};

export const updatePersona = (personaId, payload, accessToken) => {
  return put(`/v1/personas/${personaId}`, payload, accessToken);
};

export const getMyPersonas = (accessToken) => {
  return get('/v1/personas/mine', accessToken);
};

export const getTemplatePersonas = (accessToken) => {
  return get('/v1/personas/templates', accessToken);
};

export const deletePersona = (personaId, accessToken) => {
  return del(`/v1/personas/${personaId}`, accessToken);
};

// For Profiles ("Profiles of Me")
export const getMyProfiles = (accessToken) => {
  return request('/v1/profiles', { method: 'GET' }, accessToken);
};

export const getProfile = (profileId, accessToken) => {
  return request(`/v1/profiles/${profileId}`, { method: 'GET' }, accessToken);
};

export const updateProfile = (profileId, payload, accessToken) => {
  return put(`/v1/profiles/${profileId}`, payload, accessToken);
};

export const createProfile = (payload, accessToken) => {
  return post('/v1/profiles', payload, accessToken);
};

export const deleteProfile = (profileId, accessToken) => {
  return del(`/v1/profiles/${profileId}`, accessToken);
};

// For API Keys
export const createApiKey = (payload, accessToken) => {
  return post('/v1/keys', payload, accessToken);
};
export const getApiKeys = (accessToken) => {
  return get('/v1/keys', accessToken);
};
export const revokeApiKey = (keyId, accessToken) => {
  return del(`/v1/keys/${keyId}`, accessToken);
};

// MOCK API
export const createCheckoutSession = (payload, accessToken) => {
  console.log('Creating dummy checkout session with payload:', payload);

  // Simulate a network delay
  return new Promise((resolve) => {
    setTimeout(() => {
      // Return a fake session ID that looks real
      resolve({
        sessionId: 'cs_test_a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0',
      });
    }, 1000); // 1 second delay
  });
};
